// Proforma conditional sections evaluator.
// Evaluates Atlassian Proforma "conditions" (groups of checks with AND/OR operators)
// to decide whether a section — and therefore the questions it contains — is visible.
// Falls back to the legacy `i.co.cIds` shortcut when no `groups` are present.

interface ConditionCheck {
  fieldId: string;
  type: string; // SOME_OF | ONE_OF | ALL_OF | NONE_OF | NOT_EMPTY | EMPTY | EQUAL ...
  constraint?: string[];
}

interface ConditionGroup {
  operator?: 'AND' | 'OR';
  checks?: ConditionCheck[];
}

interface ConditionInput {
  co?: { cIds?: Record<string, string[]> };
  operator?: 'AND' | 'OR';
  groups?: ConditionGroup[];
}

interface ConditionOutput {
  sIds?: string[];
  t?: string; // 'sh' (show) | 'hi' (hide)
}

interface ProformaCondition {
  i?: ConditionInput;
  o?: ConditionOutput;
}

interface ProformaSection {
  conditions?: string[];
}

interface ProformaQuestion {
  type?: string;
  jiraField?: string;
}

interface LayoutNode {
  type?: string;
  attrs?: { parameters?: { id?: string | number } };
  content?: LayoutNode[];
}

interface LayoutDoc {
  content?: LayoutNode[];
}

export interface ProformaDesign {
  conditions?: Record<string, ProformaCondition>;
  sections?: Record<string, ProformaSection>;
  questions?: Record<string, ProformaQuestion>;
  layout?: LayoutDoc[];
}

interface ObjectValue {
  value?: string;
  label?: string;
}

interface FormAnswer {
  type?: string;
  value?: string | number | string[] | ObjectValue | ObjectValue[] | null;
}

export type FormAnswers = Record<string, FormAnswer | undefined>;

// Resolve the formAnswers key for a question: object (CMDB/Jira) fields are keyed
// by their jiraField, every other question type is keyed by its question id.
const getAnswer = (design: ProformaDesign | undefined, answers: FormAnswers, fieldId: string): FormAnswer | undefined => {
  const question = design?.questions?.[fieldId];
  const apiKey = question?.type === 'ob' && question?.jiraField ? question.jiraField : fieldId;
  return answers?.[apiKey];
};

// Normalize any answer to the list of selected choice ids (string form).
const getSelectedIds = (answer: FormAnswer | undefined): string[] => {
  const value = answer?.value;
  if (value == null) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item : String((item as ObjectValue)?.value ?? '')))
      .filter(Boolean);
  }
  if (typeof value === 'object') {
    return value.value ? [String(value.value)] : [];
  }
  return value === '' ? [] : [String(value)];
};

// Whether the field holds any answer (covers choices, text, attachments, objects).
const isAnswered = (answer: FormAnswer | undefined): boolean => {
  const value = answer?.value;
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Boolean(value.value);
  if (typeof value === 'string') return value.trim() !== '';
  return true; // number
};

const evaluateCheck = (design: ProformaDesign | undefined, answers: FormAnswers, check: ConditionCheck): boolean => {
  const answer = getAnswer(design, answers, check.fieldId);
  const selected = getSelectedIds(answer);
  const constraint = (check.constraint || []).map(String);

  switch (check.type) {
    case 'SOME_OF':
    case 'ONE_OF':
    case 'ANY_OF':
      return constraint.some((c) => selected.includes(c));
    case 'ALL_OF':
      return constraint.length > 0 && constraint.every((c) => selected.includes(c));
    case 'NONE_OF':
      return !constraint.some((c) => selected.includes(c));
    case 'NOT_EMPTY':
      return isAnswered(answer);
    case 'EMPTY':
    case 'IS_EMPTY':
      return !isAnswered(answer);
    case 'EQUAL':
    case 'EQUALS':
      return selected.length === 1 && constraint.includes(selected[0]);
    default:
      console.warn(`[proformaConditions] Unknown check type "${check.type}" for field ${check.fieldId}; treating as not satisfied`);
      return false;
  }
};

const evaluateGroup = (design: ProformaDesign | undefined, answers: FormAnswers, group: ConditionGroup): boolean => {
  const checks = group?.checks || [];
  if (!checks.length) return false;
  return group.operator === 'OR'
    ? checks.some((check) => evaluateCheck(design, answers, check))
    : checks.every((check) => evaluateCheck(design, answers, check));
};

const evaluateCondition = (design: ProformaDesign | undefined, answers: FormAnswers, conditionId: string): boolean => {
  const condition = design?.conditions?.[conditionId];
  if (!condition) return false;

  const input = condition.i || {};
  const groups = input.groups || [];

  if (groups.length) {
    return input.operator === 'AND'
      ? groups.every((group) => evaluateGroup(design, answers, group))
      : groups.some((group) => evaluateGroup(design, answers, group));
  }

  // Legacy fallback: cIds maps questionId -> required choice ids (SOME_OF semantics, all entries ANDed).
  const cIds = input.co?.cIds || {};
  const entries = Object.entries(cIds);
  if (!entries.length) return false;

  return entries.every(([fieldId, requiredIds]) => {
    const selected = getSelectedIds(getAnswer(design, answers, fieldId));
    return (requiredIds || []).map(String).some((id) => selected.includes(id));
  });
};

// A section is visible when its show-conditions are met (default shown if it has none)
// and none of its hide-conditions are met.
export const isProformaSectionVisible = (design: ProformaDesign | undefined, answers: FormAnswers, sectionId: string | null): boolean => {
  if (!sectionId) return true;

  const section = design?.sections?.[sectionId];
  if (!section || !section.conditions?.length) return true;

  let hasShowCondition = false;
  let showConditionMet = false;
  let hideConditionMet = false;

  section.conditions.forEach((conditionId) => {
    const condition = design?.conditions?.[conditionId];
    const met = evaluateCondition(design, answers, conditionId);
    const outputType = condition?.o?.t || 'sh';

    if (outputType === 'hi') {
      if (met) hideConditionMet = true;
    } else {
      hasShowCondition = true;
      if (met) showConditionMet = true;
    }
  });

  const shownByShowConditions = !hasShowCondition || showConditionMet;
  return shownByShowConditions && !hideConditionMet;
};

// A question is visible when the layout section that contains it is visible.
// layout[0] is the base section (always visible); layout[idx>0] maps to section String(idx).
export const isProformaQuestionVisible = (design: ProformaDesign | undefined, answers: FormAnswers, questionId: string): boolean => {
  const layouts = design?.layout || [];

  const matchesQuestion = (node: LayoutNode | undefined): boolean =>
    node?.type === 'extension' && String(node?.attrs?.parameters?.id) === String(questionId);

  const nodeHasQuestion = (node: LayoutNode | undefined): boolean => {
    if (matchesQuestion(node)) return true;
    if (node?.type === 'layoutSection') {
      return (node.content || []).some((col) => (col?.content || []).some(matchesQuestion));
    }
    return false;
  };

  for (let layoutIdx = 0; layoutIdx < layouts.length; layoutIdx++) {
    const hasQuestion = (layouts[layoutIdx]?.content || []).some(nodeHasQuestion);
    if (!hasQuestion) continue;

    const sectionId = layoutIdx > 0 ? String(layoutIdx) : null;
    return isProformaSectionVisible(design, answers, sectionId);
  }

  return true; // question not found in layout — assume visible
};
